import React, { useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BookModel, Class, Lesson } from "@/types";
import BookCard from "./BookCard";
import { Dispatch } from "@reduxjs/toolkit";
import { useNavigate } from "react-router-dom";
import { FilterSpec } from "@/redux-store/slices/bookListStore";
interface BookListProps {
  t: (key: string) => string;
  books: BookModel[];
  filteredBooks: BookModel[];
  user: any;
  dispatch: Dispatch<any>;
  classes: Class[];
  lessons: Lesson[];
  filterSpec: FilterSpec;
}

const BookList: React.FC<BookListProps> = ({
  t,
  books,
  filterSpec,
  filteredBooks,
  classes,
  lessons,
}) => {
  const bookListRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const handleProductClick = (product: BookModel) => {
    if (product.isGroup) {
      navigate(`/book-group/${product.id}`);
    } else {
      navigate(`/book-detail/${product.id}`);
    }
  };

  const groupBy = (xs: BookModel[], key: keyof BookModel) => {
    return xs.reduce<Record<string, BookModel[]>>((rv, x) => {
      const groupKey = x[key] as string;
      if (!rv[groupKey]) rv[groupKey] = [];
      rv[groupKey].push(x);
      return rv;
    }, {});
  };

  const getLessonNames = (lessonIds: string) => {
    if (!lessonIds) return [];
    const ids = lessonIds.split(",").map((id) => id.trim());
    return ids
      .map((id) => lessons.find((l) => String(l.id) === id)?.title)
      .filter(Boolean) as string[];
  };
  const getClassNames = (classIds: string) => {
    if (!classIds) return [];
    const ids = classIds.split(",").map((id) => id.trim());
    return ids
      .map((id) => classes.find((l) => String(l.id) === id)?.title)
      .filter(Boolean) as string[];
  };

  const renderBooks = () => {
    // Kitapları classOrder'a göre sırala
    const sortedBooks = [...filteredBooks].sort(
      (a, b) => (a.classOrder ?? 0) - (b.classOrder ?? 0)
    );

    // Gruplama: classIds -> kitap listesi
    const groupedBooks: Record<string, typeof filteredBooks> = {};
    sortedBooks.forEach((book) => {
      const ids = book.classIds?.split(",").map((id) => id.trim()) || [];
      ids.forEach((id) => {
        if (!groupedBooks[id]) groupedBooks[id] = [];
        groupedBooks[id].push(book);
      });
    });
    let keyNames = Object.keys(groupedBooks);
    if (filterSpec.classId) {
      keyNames = keyNames.filter((x) => x === filterSpec.classId.toString());
    }
    // ID -> İsim çevirme yardımcı fonksiyonu
    const getClassName = (id: string) =>
      classes.find((c) => String(c.id) === id)?.title || `Sınıf ${id}`;

    return (
      <div>
        {keyNames.map((classId) => {
          const bookList = groupedBooks[classId];
          const className = getClassName(classId);

          return (
            <div key={classId}>
              <h3 className="class-name">{className}</h3>
              <div className="books">
                {bookList.map((book: BookModel, index: number) => {
                  if (book.isPackage) {
                    const lessonNames = getLessonNames(book.lessonIds) || [];
                    const classNames = getClassNames(book.classIds) || [];
                    return (
                      <BookCard
                        key={`book-item-${index}`}
                        index={index}
                        lessonNames={lessonNames.join(", ")}
                        classNames={classNames.join(", ")}
                        book={book}
                        handleProductClick={handleProductClick}
                      />
                    );
                  }

                  // Normal kitap render
                  if (book.hasGroup === false) {
                    return (
                      <BookCard
                        key={`book-item-${index}`}
                        index={index}
                        book={book}
                        handleProductClick={handleProductClick}
                      />
                    );
                  }

                  return null;
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (books.length === 0) {
    return (
      <div className="no-data">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="no-data-content"
        >
          <h3 className="no-data-title">{t("no_books")}</h3>
        </motion.div>
      </div>
    );
  }

  return (
    <>
      <AnimatePresence mode="wait">
        <motion.div
          className="book-list"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <div className="grouped-books" ref={bookListRef}>
            {renderBooks()}
          </div>
        </motion.div>
      </AnimatePresence>
    </>
  );
};

export default BookList;
